const get = require('lodash/get')
const groupBy = require('lodash/groupBy')
const chunk = require('lodash/chunk')
const { getInsertProperties, getListArgs } = require('../../tools/sql-tools')
const isNumeric = (n) => Number.isFinite(Number(n))
const isStrictISOString = (value) => {
  if (!value || typeof value !== 'string') {
    return false
  }
  const date = new Date(value)
  return !Number.isNaN(date.getTime()) && date.toISOString() === value
}

class CouchProductToPg {
  constructor (pgConnection, getProductsByIds, logger) {
    this.pgConnection = pgConnection
    this.getProductsByIds = getProductsByIds
    this.logger = logger
  }

  extractPresentationUnit (product) {
    const rules = [
      { re: /\bSCREW(S)?\b/, val: 'screw' },
      { re: /\bPLATE(S)?\b/, val: 'plate' },
      { re: /\bIMPLANT\b/, val: 'implant' },
      { re: /\bSYRINGE(S)?\b/, val: 'syringe' },
      { re: /\bNEEDLE(S)?\b/, val: 'needle' },
      { re: /\bCANNULA(R)?\b|\bCANNULATED\b/, val: 'cannula' },
      { re: /\bGIVING SET\b/, val: 'giving set' },
      { re: /\bSET\b/, val: 'instrument set' },
      { re: /\bKIT\b/, val: 'kit' },
      { re: /\bMACHINE\b/, val: 'machine' },
      { re: /\bEQUIPMENT\b/, val: 'equipment' },
      { re: /\bINFUSION\b|\bIV FLUID\b|\bBAG\b/, val: 'infusion' },
      { re: /\bINJ\b|\bINJECTION\b|\bPOWDER FOR INJECTION\b/, val: 'injection' },
      { re: /\bVIAL\b|\bVL\b/, val: 'vial' },
      { re: /\bAMP\b|\bAMPOULE\b/, val: 'ampoule' },
      { re: /\bPFS\b|\bPREFILLED\b/, val: 'prefilled syringe' },
      { re: /\bINHALATION\b|\bINHALER\b/, val: 'inhaler' },
      { re: /\bNEBULISER\b|\bNEBULIZER\b/, val: 'nebulizer solution' },
      { re: /\bAEROSOL\b|\bMETERED DOSE\b/, val: 'spray' },
      { re: /\bEYE DROP(S)?\b|\bOPHTH\b/, val: 'eye drop' },
      { re: /\bEYE OINTMENT\b/, val: 'eye ointment' },
      { re: /\bEAR DROP(S)?\b/, val: 'ear drop' },
      { re: /\bNASAL DROP(S)?\b/, val: 'nasal drop' },
      { re: /\bNASAL SPRAY\b/, val: 'nasal spray' },
      { re: /\bCREAM\b/, val: 'cream' },
      { re: /\bOINTMENT\b/, val: 'ointment' },
      { re: /\bGEL\b/, val: 'gel' },
      { re: /\bLOTION\b/, val: 'lotion' },
      { re: /\bPASTE\b/, val: 'paste' },
      { re: /\bSUPPOSITORY\b/, val: 'suppository' },
      { re: /\bVAGINAL TABLET\b/, val: 'vaginal tablet' },
      { re: /\bPESSAR(Y|IES)\b/, val: 'pessary' },
      { re: /\bVAGINAL CREAM\b/, val: 'vaginal cream' },
      { re: /\bSYRUP\b/, val: 'syrup' },
      { re: /\bSUSP(ENSION)?\b/, val: 'suspension' },
      { re: /\bORAL SOLUTION\b/, val: 'oral solution' },
      { re: /\bSOLUTION\b/, val: 'solution' },
      { re: /\bSACHET\b/, val: 'sachet' },
      { re: /\bPOWDER\b/, val: 'powder' },
      { re: /\bDISP(ERSIBLE)?\b/, val: 'dispersible tablet' },
      { re: /\bFILM[- ]?COAT(ED)?\b/, val: 'film coated tablet' },
      { re: /\bCAP(SULE)?\b/, val: 'capsule' },
      { re: /\bTAB(LET)?S?\b/, val: 'tablet' },
      { re: /\bORAL REHYDRATION SALT\b|\bORS\b/, val: 'oral rehydration salt' },
      { re: /\bTHERAPEUTIC\b/, val: 'therapeutic food' },
      { re: /\bPARENTERAL NUTRITION\b/, val: 'parenteral nutrition' }
    ]

    for (const rule of rules) {
      if (rule.re.test(`${product.fullName}`.toUpperCase())) {
        return rule.val
      }
    }

    return product.basicUnit
  }

  async resolveBase (pgClient, base) {
    const { rows: [existing] } = await pgClient.query(`
      select id
      from avocado.data_productbase
      where name is not distinct from $1
        and manufacturer is not distinct from $2
        and dosage_qty is not distinct from $3
        and dosage_unit is not distinct from $4
      order by id
      limit 1
    `, [base.name, base.manufacturer, base.dosage_qty, base.dosage_unit])

    if (existing) {
      return existing
    }

    const { columns, placeholders, values } = getInsertProperties([base])
    const { rows: [created] } = await pgClient.query(`
      insert into avocado.data_productbase (${columns})
      values ${placeholders}
      returning id
    `, values)

    return created
  }

  async resolvePresentation (pgClient, presentation) {
    const { rows: [existing] } = await pgClient.query(`
      select id
      from avocado.data_productpresentation
      where product_id is not distinct from $1
        and presentation_qty is not distinct from $2
        and presentation_unit is not distinct from $3
        and packaging_type is not distinct from $4
        and presentation_type is not distinct from $5
      order by id
      limit 1
    `, [
      presentation.product_id,
      presentation.presentation_qty,
      presentation.presentation_unit,
      presentation.packaging_type,
      presentation.presentation_type
    ])

    if (existing) {
      await pgClient.query(`
        update avocado.data_productpresentation
        set
          weight = $1,
          volume = $2,
          updated_at = now()
        where id = $3
          and (weight is distinct from $1 or volume is distinct from $2)
      `, [presentation.weight, presentation.volume, existing.id])

      return existing
    }

    const { columns, placeholders, values } = getInsertProperties([presentation])
    const { rows: [created] } = await pgClient.query(`
      insert into avocado.data_productpresentation (${columns})
      values ${placeholders}
      returning id
    `, values)

    return created
  }

  async mapParentPresentations (pgClient, parentPresentationMappings) {
    const parentSkuIds = []
    const childSkuIds = []
    for (const [parentOldSkuId, childOldSkuIds] of Object.entries(parentPresentationMappings)) {
      for (const childOldSkuId of childOldSkuIds) {
        parentSkuIds.push(parentOldSkuId)
        childSkuIds.push(childOldSkuId)
      }
    }

    if (parentSkuIds.length) {
      await pgClient.query(`
        with presentation_links as (
          select
            parent_skus.presentation_id as parent_presentation_id,
            child_skus.presentation_id as child_presentation_id,
            parent_skus.old_sku_id as parent_sku,
            child_skus.old_sku_id as child_sku
          from unnest($1::text[], $2::text[]) as links(parent_old_sku_id, child_old_sku_id)
          inner join avocado.data_productsku parent_skus on parent_skus.old_sku_id = links.parent_old_sku_id
          inner join avocado.data_productsku child_skus on child_skus.old_sku_id = links.child_old_sku_id
          where parent_skus.presentation_id is not null
            and child_skus.presentation_id is not null
        )
        update avocado.data_productpresentation p
        set
          parent_presentation_id = pl.parent_presentation_id,
          updated_at = now()
        from presentation_links pl
        where p.id = pl.child_presentation_id
          and p.parent_presentation_id is distinct from pl.parent_presentation_id
        returning *
      `, [parentSkuIds, childSkuIds])
    }
  }

  async mapSkusToServices (pgClient, serviceIds, rows) {
    if (!rows || !rows.length) {
      return
    }
    const { args } = getListArgs(serviceIds)
    const { rows: services } = await pgClient.query(`
      select
        id,
        fsid
      from avocado.data_service
      where fsid in (${args})
    `, serviceIds)
    const servicesByFsid = groupBy(services, 'fsid')
    const missingServices = []
    const affectedSkus = []
    const parsedRows = rows.map(r => {
      const serviceId = get(servicesByFsid, `${r.service_id}.0.id`, null)
      if (!serviceId) {
        missingServices.push(r.service_id)
        affectedSkus.push(r.sku_id)
      }
      return {
        sku_id: r.sku_id,
        index: r.index,
        tracer: r.tracer,
        warehouse_sku_id: r.warehouse_sku_id,
        service_product_line_id: null,
        service_id: serviceId
      }
    }).filter(r => r.service_id)

    if (missingServices.length) {
      this.logger.warn(
        'Service not found, related product sku service line relationship won\'t be mapped',
        {
          code: 'service-not-found-related-product-sku-service-line-relationship-wont-be-mapped',
          missingServices,
          affectedSkus
        }
      )
    }

    if (!parsedRows.length) {
      return
    }

    // Upsert services in batches
    const batchedRows = chunk(parsedRows, 500)
    for (const rows of batchedRows) {
      const { columns, values, placeholders } = getInsertProperties(rows)
      await pgClient.query(
        `
          with insert_rows as (
            insert into avocado.data_productsku_service (${columns})
            values ${placeholders}
            ON CONFLICT (sku_id, service_id) DO UPDATE
            SET
              updated_at = now(),
              tracer = excluded.tracer,
              warehouse_sku_id = excluded.warehouse_sku_id,
              index = excluded.index
            returning *
          ),
          diffed_rows as (
            select
              ps.id
            from insert_rows ir
            right join avocado.data_productsku_service ps on ir.sku_id = ps.sku_id and ir.service_id = ps.service_id
            where ps.sku_id in (select sku_id from insert_rows)
          ),
          delete_rows as (
            delete from avocado.data_productsku_service ps
            where ps.id in (
              select id from diffed_rows
            )
            returning ps.id
          )
          select * from delete_rows
        `,
        values
      )
    }
  }

  isVirtualProduct (productCode, genericParent) {
    return productCode.toLowerCase() === genericParent.toLowerCase()
  }

  mapPresentation (product, presentationField, productCode) {
    return {
      created_at: 'now()',
      updated_at: 'now()',
      presentation_unit: this.extractPresentationUnit(product),
      presentation_qty: get(product, `${presentationField}`, null),
      parent_presentation_pack_size: null,
      parent_presentation_id: null,
      packaging_type: product.basicUnit,
      presentation_type: product.basicUnit,
      weight: isNumeric(product.unitWeight) ? Number.parseFloat(product.unitWeight) : null,
      volume: isNumeric(product.unitVolume) ? Number.parseFloat(product.unitVolume) : null,
      weight_unit: null,
      volume_unit: null
    }
  }

  mapPriceHistory (product) {
    const priceHistory = {}
    const sellPrices = get(product, 'prices', [])
    const buyPrices = get(product, 'buyPrices', [])

    for (const entry of sellPrices) {
      if (isStrictISOString(entry.date)) {
        if (!isNaN(parseFloat(entry.price))) {
          if (!priceHistory[entry.date]) {
            priceHistory[entry.date] = { sellprice: parseFloat(entry.price), buyprice: null, date: entry.date }
          } else {
            priceHistory[entry.date].sellprice = parseFloat(entry.price)
          }
        }
      }
    }

    for (const entry of buyPrices) {
      if (isStrictISOString(entry.date)) {
        if (!isNaN(parseFloat(entry.price))) {
          if (!priceHistory[entry.date]) {
            priceHistory[entry.date] = { sellprice: null, buyprice: parseFloat(entry.price), date: entry.date }
          } else {
            priceHistory[entry.date].buyprice = parseFloat(entry.price)
          }
        }
      }
    }
    return Object.values(priceHistory)
  }

  mapSku (product, productCode, genericParent, addUnitToOldSkuId = false) {
    const skuId = `${productCode}${addUnitToOldSkuId ? `-${product.basicUnit}` : ''}`.replace(/\s+/g, '')
    return {
      created_at: 'now()',
      updated_at: 'now()',
      sku_id: null,
      old_sku_id: skuId,
      order_sku: genericParent, // used in product sync integration test to determine product mappings
      name: product.name,
      display_name: product.fullName,
      inventory_sensitivity: null,
      display_order: null,
      phase: null,
      available: true,
      ready: true,
      promoted: null,
      ppmv: false,
      ppmv_core: false,
      supplier: null,
      // This is required field for the table
      visibility: 'High',
      markup: 0,
      vat: false,
      eligible_for_od: true,
      eligible_for_pays: true,
      segments: null,
      _deleted: false,
      essential_medicines: false
    }
  }

  mapServices (product, skuId) {
    let services = []
    const isTracer = `${get(product, 'tracer', false)}` === 'true'
    if (Array.isArray(product.services)) {
      services = product.services.map(service => {
        const serviceFsid = get(service, 'id', null)
        return {
          index: get(service, 'index', null),
          service_id: serviceFsid,
          tracer: isTracer,
          warehouse_sku_id: null,
          sku_id: skuId
        }
      })
    }
    return services
  }

  generatePresentationSkusAndServices (product, productCode, genericParent, prices) {
    const presentationSkus = []
    const childPresentations = []
    const equalUnitOfReportingAndUnitOfIssue = product.unitOfReporting === product.unitOfIssue

    // Tied primarily to the facility
    // We only sync this presentation sku when the unit of reporting is not equal to the unit of issue
    if (product.unitOfReporting && !equalUnitOfReportingAndUnitOfIssue) {
      const sku = this.mapSku(product, productCode, genericParent, true)
      childPresentations.push(sku.old_sku_id)
      presentationSkus.push({
        presentation: this.mapPresentation(product, 'unitOfReporting', productCode),
        sku,
        services: this.mapServices(product, sku.old_sku_id),
        isParentPresentation: false,
        childPresentations: [],
        prices: prices.map(entry => ({ ...entry, sku: sku.old_sku_id }))
      })
    }

    // Tied primarily to the warehouse / supplier
    if (product.unitOfIssue) {
      const sku = this.mapSku(product, productCode, genericParent)
      presentationSkus.push({
        presentation: this.mapPresentation(product, 'unitOfIssue', productCode),
        sku,
        services: this.mapServices(product, sku.old_sku_id),
        isParentPresentation: true,
        childPresentations,
        prices: prices.map(entry => ({ ...entry, sku: sku.old_sku_id }))
      })
    }
    return presentationSkus
  }

  /**
   * @param {string[]} productIds
   */
  async sync (productIds) {
    const couchProducts = await this.getProductsByIds(productIds)
    const productServicesSynced = new Set()

    if (!couchProducts.length) {
      return []
    }

    const skuRelationshipGraph = couchProducts.reduce((acc, product) => {
      const productCode = product._id.replace('product:', '')
      // we fall back to the product code if no generic parent is specified
      const genericParent = get(product, 'genericParent', '').toLowerCase() || productCode.toLowerCase()
      const productName = get(product, 'fullName', '')
      const baseProductName = get(product, 'name', '')
      const manufacturer = get(product, 'manufacturer', null)
      const dosageQty = get(product, 'dosageQty', null)
      const dosageUnit = get(product, 'dosageUnit', null)
      const isVirtualProduct = this.isVirtualProduct(productCode, genericParent)
      const prices = this.mapPriceHistory(product)
      const presentationSkus = this.generatePresentationSkusAndServices(product, productCode, genericParent, prices)
      presentationSkus.forEach(ps => {
        ps.services.forEach(s => {
          productServicesSynced.add(s.service_id)
        })
      })

      acc[productCode] = {
        virtual: [
          {
            generic_id: genericParent,
            name: isVirtualProduct ? productName : '',
            isVirtualProduct
          }
        ],
        base: [
          {
            created_at: 'now()',
            updated_at: 'now()',
            name: baseProductName,
            manufacturer,
            dosage_qty: dosageQty,
            dosage_unit: dosageUnit,
            virtual_product_id: null
          }
        ],
        presentationSkus
      }
      return acc
    }, {})

    const skusSynced = []
    const productServices = []
    const parentPresentationMappings = {}
    const pgClient = await this.pgConnection.connect()
    try {
      await pgClient.query('BEGIN')
      for (const product of Object.values(skuRelationshipGraph)) {
        const virtualProduct = product.virtual[0]
        const { columns: columns0, placeholders: placeholders0, values: values0 } = getInsertProperties([{ generic_id: virtualProduct.generic_id, name: virtualProduct.name }])
        const { rows: [virtualSku] } = await pgClient.query(`
          insert into avocado.data_virtual_product (${columns0})
          values ${placeholders0}
          ON CONFLICT (generic_id) DO UPDATE
          set
            -- only update name if it's a virtual product in case of difference in manufacturer specific product
            ${virtualProduct.isVirtualProduct ? ' name = excluded.name, ' : ''}
            updated_at = now()
          returning id
        `, values0)

        const productBase = await this.resolveBase(
          pgClient,
          { ...product.base[0], virtual_product_id: virtualSku.id }
        )

        for (const { presentation, sku, services, isParentPresentation, childPresentations, prices } of product.presentationSkus) {
          const savedPresentation = await this.resolvePresentation(
            pgClient,
            { ...presentation, product_id: productBase.id }
          )

          const { columns, placeholders, values } = getInsertProperties([{ ...sku, presentation_id: savedPresentation.id }])
          const { rows: [savedSku] } = await pgClient.query(`
            insert into avocado.data_productsku (${columns})
            values ${placeholders}
            ON CONFLICT (old_sku_id) DO UPDATE
            set
              name = excluded.name,
              display_name = excluded.display_name,
              presentation_id = excluded.presentation_id,
              updated_at = now()
            returning old_sku_id, id
          `, values)
          skusSynced.push(savedSku)
          if (isParentPresentation) {
            parentPresentationMappings[savedSku.old_sku_id] = childPresentations
          }

          // sync product prices to real.price_history table
          if (prices.length > 0) {
            const { columns: columns3, placeholders: placeholders3, values: values3 } = getInsertProperties(prices)
            await pgClient.query(`
              insert into real.price_history (${columns3})
              values ${placeholders3}
              ON CONFLICT (sku, date)
              WHERE service_id IS NULL AND location_id IS NULL
              DO UPDATE
               SET
                sellprice = EXCLUDED.sellprice,
                buyprice = EXCLUDED.buyprice,
                updated_at = now()
              returning id
            `, values3)
          }
          productServices.push(...services)
        }
      }

      await this.mapParentPresentations(pgClient, parentPresentationMappings)
      await this.mapSkusToServices(pgClient, [...productServicesSynced], productServices)
      await pgClient.query('COMMIT')
    } catch (error) {
      await pgClient.query('ROLLBACK')
      throw error
    } finally {
      pgClient.release()
    }

    return skusSynced
  }
}

module.exports = { CouchProductToPg }
